import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserRoles } from '../../hooks/useUserRoles';

export interface HelpContext {
  page: string;
  module: string;
  section?: string;
  action?: string;
  userRole: string[];
}

export interface HelpContent {
  id: string;
  title: string;
  description: string;
  content: string;
  type: 'guide' | 'tutorial' | 'reference' | 'troubleshooting';
  tags: string[];
  context: {
    pages?: string[];
    modules?: string[];
    roles?: string[];
    sections?: string[];
  };
  priority: number;
  lastUpdated: Date;
  videoUrl?: string;
  relatedArticles?: string[];
}

interface HelpContextState {
  currentContext: HelpContext;
  availableHelp: HelpContent[];
  isHelpVisible: boolean;
  activeHelpId?: string;
  searchQuery: string;
  setContext: (context: Partial<HelpContext>) => void;
  showHelp: (helpId?: string) => void;
  hideHelp: () => void;
  searchHelp: (query: string) => void;
  getContextualHelp: () => HelpContent[];
  getHelpByType: (type: HelpContent['type']) => HelpContent[];
}

const HelpContextContext = createContext<HelpContextState | undefined>(undefined);

// Mock help content - in real app, this would come from API/CMS
const mockHelpContent: HelpContent[] = [
  {
    id: 'dashboard-overview',
    title: 'Vue d\'ensemble du tableau de bord',
    description: 'Comprendre les widgets et métriques de votre tableau de bord',
    content: `# Tableau de bord PLV+

Le tableau de bord vous donne une vue d'ensemble de vos activités et performances.

## Widgets disponibles

### Métriques générales
- **Utilisateurs actifs** : Nombre d'utilisateurs connectés aujourd'hui
- **Modules actifs** : État des modules PLV+ disponibles
- **Performance système** : Indicateurs de santé du système

### PRI+ Overview
- **Calculs totaux** : Nombre total de calculs de prix effectués
- **Calculs mensuels** : Calculs réalisés ce mois-ci
- **Marge moyenne** : Marge bénéficiaire moyenne sur vos projets
- **Chiffre d'affaires** : Revenus générés

## Personnalisation

Vous pouvez personnaliser l'affichage de votre tableau de bord selon votre rôle :
- **Utilisateur** : Vue complète avec actions rapides
- **Manager** : Vue compacte centrée sur les métriques d'équipe
- **Admin** : Vue système avec indicateurs techniques`,
    type: 'guide',
    tags: ['dashboard', 'overview', 'widgets', 'metrics'],
    context: {
      pages: ['/dashboard'],
      modules: ['host'],
      roles: ['user', 'manager', 'admin']
    },
    priority: 10,
    lastUpdated: new Date('2024-01-20'),
    relatedArticles: ['widgets-customization', 'role-permissions']
  },
  {
    id: 'pri-plus-calculations',
    title: 'Calculs de prix de revient PRI+',
    description: 'Guide complet pour créer et gérer vos calculs de prix',
    content: `# Calculs PRI+

Le module PRI+ vous permet de calculer précisément vos prix de revient pour les projets PLV.

## Créer un nouveau calcul

1. **Informations générales**
   - Nom du projet
   - Client
   - Description

2. **Coûts matériaux**
   - Sélectionnez les matériaux depuis la base de données
   - Définissez les quantités
   - Les prix unitaires sont automatiquement récupérés

3. **Coûts de main-d'œuvre**
   - Rôles nécessaires (graphiste, installateur, etc.)
   - Temps estimé par rôle
   - Taux horaires

4. **Frais généraux**
   - Frais fixes (loyer, assurance, etc.)
   - Frais variables (transport, outillage, etc.)
   - Pourcentage sur le coût direct

5. **Marge et prix de vente**
   - Définissez votre marge souhaitée
   - Le prix de vente est calculé automatiquement

## Bonnes pratiques

- **Utilisez les modèles** : Créez des modèles pour les types de projets récurrents
- **Vérifiez les prix** : Mettez à jour régulièrement les prix des matériaux
- **Analysez vos marges** : Suivez l'évolution de vos marges par type de projet`,
    type: 'tutorial',
    tags: ['pri-plus', 'calculations', 'pricing', 'materials', 'labor'],
    context: {
      pages: ['/pri-plus/calculations', '/pri-plus/calculations/new'],
      modules: ['pri-plus'],
      roles: ['user', 'manager']
    },
    priority: 9,
    lastUpdated: new Date('2024-01-22'),
    videoUrl: 'https://example.com/videos/pri-plus-tutorial',
    relatedArticles: ['materials-database', 'pricing-templates', 'margin-analysis']
  },
  {
    id: 'pri-sheets-management',
    title: 'Gestion des fiches PRI',
    description: 'Comment créer et gérer vos fiches de spécifications techniques',
    content: `# Fiches PRI (Spécifications techniques)

Les fiches PRI permettent de documenter précisément les spécifications techniques de vos projets PLV.

## Création d'une fiche PRI

### Informations générales
- **Nom de la fiche** : Titre descriptif du projet
- **Client** : Nom du client final
- **Projet** : Nom du projet ou campagne
- **Quantité** : Nombre d'éléments à produire
- **Dates** : Livraison et installation prévues

### Spécifications techniques
Chaque fiche peut contenir plusieurs spécifications :

#### Dimensions
- Largeur, hauteur, profondeur
- Unité de mesure (mm, cm, m)
- Poids estimé

#### Matériaux
- Type de matériau principal
- Finition (laquage, impression, etc.)
- Couleurs

#### Technique
- Méthode d'impression (numérique, offset, sérigraphie)
- Résolution d'impression
- Pelliculage ou protection

#### Installation
- Type d'installation (murale, au sol, suspendue)
- Méthode de fixation
- Notes spéciales d'installation

#### Conformité
- Classement feu si requis
- Certifications nécessaires (CE, NF, etc.)

## Workflow de validation

1. **Brouillon** : Création et modification libre
2. **En révision** : Soumis pour validation technique
3. **Approuvé** : Validé pour production
4. **En production** : Fabrication en cours
5. **Terminé** : Projet livré et installé

## Intégration avec PRI+

Les fiches PRI peuvent être liées aux calculs de prix :
- Génération automatique de calcul depuis une fiche
- Création de devis basé sur les spécifications
- Suivi des coûts réels vs estimés`,
    type: 'guide',
    tags: ['pri-sheets', 'specifications', 'technical', 'workflow', 'plv'],
    context: {
      pages: ['/pri-plus/pri-sheets', '/pri-plus/pri-sheets/new'],
      modules: ['pri-plus'],
      roles: ['user', 'manager']
    },
    priority: 8,
    lastUpdated: new Date('2024-01-25'),
    relatedArticles: ['pri-plus-calculations', 'project-workflow', 'technical-specs']
  },
  {
    id: 'user-roles-permissions',
    title: 'Rôles et permissions utilisateur',
    description: 'Comprendre les différents niveaux d\'accès dans PLV+',
    content: `# Système de rôles PLV+

PLV+ utilise un système de rôles pour contrôler l'accès aux fonctionnalités selon votre niveau de responsabilité.

## Types de rôles

### Utilisateur (User)
**Accès :** Fonctionnalités de base
- Création et modification de ses propres calculs
- Consultation des matériaux et modèles
- Création de fiches PRI
- Vue personnelle du tableau de bord

### Manager
**Accès :** Gestion d'équipe + fonctionnalités utilisateur
- Validation des calculs de son équipe
- Accès aux rapports d'équipe
- Gestion des modèles partagés
- Vue manager du tableau de bord

### Administrateur (Admin)
**Accès :** Administration système + toutes les fonctionnalités
- Gestion des utilisateurs et rôles
- Configuration des modules
- Accès aux logs système
- Gestion de la base de données matériaux
- Vue admin du tableau de bord

## Permissions par module

### Module Host (Portail)
- **Tous** : Accès au tableau de bord personnel
- **Manager** : Vue des KPIs d'équipe
- **Admin** : Métriques système et gestion utilisateurs

### Module PRI+
- **User** : CRUD sur ses calculs et fiches PRI
- **Manager** : Validation et supervision d'équipe
- **Admin** : Configuration des paramètres de calcul

## Demande de droits

Pour demander des droits supplémentaires :
1. Contactez votre manager ou administrateur
2. Justifiez le besoin métier
3. L'attribution est validée selon la politique de sécurité`,
    type: 'reference',
    tags: ['roles', 'permissions', 'security', 'access', 'admin'],
    context: {
      pages: ['/settings', '/admin'],
      modules: ['host'],
      roles: ['user', 'manager', 'admin']
    },
    priority: 6,
    lastUpdated: new Date('2024-01-18'),
    relatedArticles: ['security-policy', 'user-management']
  },
  {
    id: 'troubleshooting-common',
    title: 'Résolution des problèmes courants',
    description: 'Solutions aux problèmes fréquemment rencontrés',
    content: `# Dépannage PLV+

## Problèmes de connexion

### Impossible de se connecter
**Symptômes :** Page de connexion en boucle, erreur d'authentification

**Solutions :**
1. Vérifiez vos identifiants
2. Videz le cache du navigateur (Ctrl+Shift+Del)
3. Essayez en navigation privée
4. Contactez l'administrateur si le problème persiste

### Session expirée fréquemment
**Cause :** Paramètres de sécurité ou inactivité

**Solutions :**
1. Sauvegardez votre travail régulièrement
2. Utilisez l'option "Rester connecté" si disponible
3. Vérifiez la stabilité de votre connexion internet

## Problèmes de calcul PRI+

### Calcul incorrect des totaux
**Vérifications :**
1. Prix des matériaux à jour dans la base
2. Taux horaires correctement configurés
3. Pourcentages de marge bien saisis
4. Quantités cohérentes

### Matériaux introuvables
**Solutions :**
1. Vérifiez l'orthographe dans la recherche
2. Utilisez les filtres par catégorie
3. Contactez l'admin pour ajouter de nouveaux matériaux

## Problèmes d'affichage

### Interface déformée ou illisible
**Solutions :**
1. Actualisez la page (F5)
2. Vérifiez le niveau de zoom (100% recommandé)
3. Testez avec un autre navigateur
4. Désactivez temporairement les extensions

### Lenteurs de chargement
**Optimisations :**
1. Fermez les onglets inutiles
2. Vérifiez votre connexion internet
3. Videz le cache navigateur
4. Redémarrez le navigateur

## Obtenir de l'aide

Si ces solutions ne résolvent pas votre problème :
1. **Assistant IA** : Posez votre question à l'assistant intégré
2. **Documentation** : Consultez les guides détaillés
3. **Support** : Contactez l'équipe technique via le chat`,
    type: 'troubleshooting',
    tags: ['troubleshooting', 'problems', 'solutions', 'technical', 'support'],
    context: {
      pages: ['*'],
      modules: ['host', 'pri-plus'],
      roles: ['user', 'manager', 'admin']
    },
    priority: 7,
    lastUpdated: new Date('2024-01-24'),
    relatedArticles: ['technical-support', 'system-requirements']
  }
];

interface HelpContextProviderProps {
  children: ReactNode;
}

export const HelpContextProvider: React.FC<HelpContextProviderProps> = ({ children }) => {
  const location = useLocation();
  const { user } = useUserRoles();
  
  const [currentContext, setCurrentContext] = useState<HelpContext>({
    page: '/',
    module: 'host',
    userRole: user?.roles.map(r => r.level) || []
  });
  
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [activeHelpId, setActiveHelpId] = useState<string>();
  const [searchQuery, setSearchQuery] = useState('');

  // Update context when location or user changes
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const module = pathSegments[0] || 'host';
    const section = pathSegments[1];
    const action = pathSegments[2];

    setCurrentContext({
      page: location.pathname,
      module,
      section,
      action,
      userRole: user?.roles.map(r => r.level) || []
    });
  }, [location.pathname, user]);

  const setContext = (context: Partial<HelpContext>) => {
    setCurrentContext(prev => ({ ...prev, ...context }));
  };

  const showHelp = (helpId?: string) => {
    setIsHelpVisible(true);
    setActiveHelpId(helpId);
  };

  const hideHelp = () => {
    setIsHelpVisible(false);
    setActiveHelpId(undefined);
  };

  const searchHelp = (query: string) => {
    setSearchQuery(query);
  };

  const getContextualHelp = (): HelpContent[] => {
    return mockHelpContent
      .filter(help => {
        // Filter by page context
        const pageMatch = !help.context.pages || 
          help.context.pages.some(page => 
            page === '*' || 
            currentContext.page === page || 
            currentContext.page.startsWith(page)
          );

        // Filter by module context
        const moduleMatch = !help.context.modules || 
          help.context.modules.includes(currentContext.module);

        // Filter by role context
        const roleMatch = !help.context.roles || 
          help.context.roles.some(role => currentContext.userRole.includes(role));

        // Filter by search query
        const searchMatch = !searchQuery || 
          help.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          help.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          help.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

        return pageMatch && moduleMatch && roleMatch && searchMatch;
      })
      .sort((a, b) => b.priority - a.priority);
  };

  const getHelpByType = (type: HelpContent['type']): HelpContent[] => {
    return getContextualHelp().filter(help => help.type === type);
  };

  const value: HelpContextState = {
    currentContext,
    availableHelp: mockHelpContent,
    isHelpVisible,
    activeHelpId,
    searchQuery,
    setContext,
    showHelp,
    hideHelp,
    searchHelp,
    getContextualHelp,
    getHelpByType
  };

  return (
    <HelpContextContext.Provider value={value}>
      {children}
    </HelpContextContext.Provider>
  );
};

export const useHelpContext = (): HelpContextState => {
  const context = useContext(HelpContextContext);
  if (!context) {
    throw new Error('useHelpContext must be used within a HelpContextProvider');
  }
  return context;
};
